import React, { useCallback, useEffect, useMemo, useState, } from 'react';

import { useTranslation, } from 'react-i18next';
import { useNavigate, } from 'react-router-dom';
import {
  ListAddForm,
  ListAddInitialValues,
  ListComponents,
  ListAdditionalParams,
  ListFormOptions,
  onListFormSaved,
  IListFormOptionsProps,
  AlertError,
  queryParam,
} from '@Utils';
import { AtlasForm, IFormOptions, } from 'atlas-form';
import { CrudEnum, ISharedCreateList, PermissionEnum, } from 'atlas-shared';
import { createList, fetchList, getListCreateSchema, } from '@Api';
import { RuleProtected, } from '@Containers';
import { withAdd, IWithAddProps, } from '@Hocs/with.add';
import { useAbilities, useAuth, useOrganizations, useForms, } from '@Hooks';
import { AppSpinner, } from '@Components';
import { useAppDispatch, } from '@Store';

interface IProps extends IWithAddProps<ISharedCreateList> {}

const ListFormAdd = ({ values, setValues, save, formSchema, schemaLoading, saving, initialValues, title, onSaved, onCancel, onChange, hasAdvanced = false, }: IProps) => {
  const organizations = useOrganizations();
  const forms = useForms();
  const abilities = useAbilities();
  const { t, } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const dispatch = useAppDispatch();

  const [cloned, setCloned, ] = useState<Partial<ISharedCreateList>>();

  const param_props: IListFormOptionsProps = {
    t,
    is_new: true,
    abilities,
    organizations: organizations.organizations,
    forms: forms.forms,
  };
  const formLayout = useMemo(() => ListAddForm(values, param_props, t), [values, organizations, forms, t, abilities, ]);
  const options: IFormOptions = useMemo(() => ListFormOptions(values || ListAddInitialValues, param_props, t), [values, organizations, forms, t, abilities, ]);
  
  const onValuesChange = useCallback((changedValue: any, values: ISharedCreateList, form) => {
    setValues(values);
    onChange?.(values, changedValue, form);
  }, [setValues, ]);
  const onFinish = useCallback((values: ISharedCreateList) => save(() => createList(values).then(
    res => onSaved ? onSaved(res, navigate) : onListFormSaved(t, 'add', res, navigate),
    err => {
      AlertError(t, {
        content: err,
      });
    }
  )), [save, navigate, t, ]);
  
  useEffect(() => {
    const id = queryParam('clone');

    if (id)
      fetchList(+id)
        .then(({
          organization_id,
          title,
          form_id,
          description,
        }) => {
          setCloned({
            organization_id,
            title,
            form_id,
            description,
          });
        });
    else
      setCloned({});
  }, []);
  
  if (schemaLoading || !cloned)
    return <AppSpinner />;

  if (!formSchema)
    return <>{t('SCHEMA_NOT_FOUND')}</>;
  
  if (!organizations.loaded || !forms.loaded)
    return <>{t('LOADING_DEPENDENCIES')}</>;

  return (
    <RuleProtected
      isAllowed={() => !values || abilities.can(values.organization_id, CrudEnum.Insert, PermissionEnum.List, values)}
    >
      <AtlasForm
        dispatch={dispatch}
        formLayout={formLayout}
        form={formSchema}
        options={options}
        components={ListComponents}
        additionalParams={ListAdditionalParams(auth, param_props)}
        onFinish={onFinish}
        onCancel={onCancel}
        onValuesChange={onValuesChange}
        initialValues={{ ...(cloned || {}), ...ListAddInitialValues, ...initialValues, }}
        saving={saving}
        title={title ? t(title) : `${t('ADD')} ${t('LIST')}`}
        hasAdvanced={hasAdvanced}
      />
    </RuleProtected>
  );
};

export const ListFormAddView = React.memo(withAdd(ListFormAdd, getListCreateSchema, ListAddInitialValues));
